// ✅ SalesPanel.jsx con loading en lugar de total y desglose de subtotal/descuento/total
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Paper, List, ListItem, Typography, Divider,
  IconButton, Box, TextField, Button, CircularProgress, Chip, Alert
} from '@mui/material';
import { getIcon } from '../../../utils/IconHandler';
import BillingModal from './billing_modal/BillingModal';
import { validateDiscountCode } from '../../../services/DiscountService';
import { calculateDiscountedPrices, checkPendingInvoice, fetchInvoiceDetails, getStoreCredit } from '../../../services/SalesService';
import UndefinedContentModal from './billing_modal/UndefinedContentModal';
import debounce from 'lodash.debounce';
import { getOptionalSelectionsList, getOptionalSelectionName } from '../../../utils/optionalSelections';
import { formatMoney } from '../../../utils/currency';

import { ticketCurrency } from './ticketCurrency';
const SalesPanel = ({ locationId, items, onUpdate, initialStoreCreditCode = null, onStoreCreditConsumed }) => {
  
  const [undefinedModalOpen, setUndefinedModalOpen] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const currency = ticketCurrency(items);
  const [discountValid, setDiscountValid] = useState(null);
  const [discountData, setDiscountData] = useState(null);
  const [discountMessage, setDiscountMessage] = useState(null); // 'valid' | 'invalid' | 'requires_quantity'
  const [discountTouched, setDiscountTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const prevItemsRef = useRef([]);
  const [ignoreNextEffect, setIgnoreNextEffect] = useState(false);
  const [summary, setSummary] = useState({ subtotal: 0, discount: 0, total: 0 });
  const [appliedStoreCredit, setAppliedStoreCredit] = useState(0);
  const debouncedRecalculateRef = useRef(null);

  useEffect(() => {
    let active = true;

    if (!initialStoreCreditCode || summary.total <= 0) {
      setAppliedStoreCredit(0);
      return () => { active = false; };
    }

    getStoreCredit(initialStoreCreditCode)
      .then((credit) => {
        if (active) setAppliedStoreCredit(Math.min(Number(credit?.remainingBalance || 0), summary.total));
      })
      .catch(() => {
        if (active) setAppliedStoreCredit(0);
      });

    return () => { active = false; };
  }, [initialStoreCreditCode, summary.total]);

  useEffect(() => {
    const checkPending = async () => {
      try { 
        const terminalCode = localStorage.getItem('terminalId');
        if (!terminalCode) return;
        const pending = await checkPendingInvoice(locationId, terminalCode);
        if (pending.hasPending && pending.invoiceId) {
          const invoice = await fetchInvoiceDetails(pending.invoiceId);
          setPendingInvoice(invoice);
          setUndefinedModalOpen(true);
        }
      } catch (err) {
        console.error('Error checking pending invoice:', err);
      }
    };
  
    checkPending();
  }, [locationId]);

  const mergePricesWithCart = (cartItems, productList, backendDiscountTotal = undefined) => {
    let subtotal = 0;
    let discountTotal = 0;

    const updatedCart = cartItems.map(cartItem => {
      const updated = productList.find(p => p.id === cartItem.id);
      if (updated) {
        const baseUnitPrice = updated.unitPrice ?? updated.unit_price;
        const unitPrice = (cartItem.optionalSelections?.length > 0 && cartItem.unitPrice != null)
          ? cartItem.unitPrice
          : baseUnitPrice;
        const discountAmount = updated.discountAmount ?? updated.discount_amount ?? 0;
        const appliedCode = updated.appliedDiscountCode ?? updated.applied_discount_code;
        const lineSubtotal = unitPrice * cartItem.qty;
        const lineDiscount = discountAmount * cartItem.qty;
        subtotal += lineSubtotal;
        discountTotal += lineDiscount;

        return {
          ...cartItem,
          unitPrice,
          discountAmount,
          discountCode: appliedCode
        };
      }
      return cartItem;
    });

    const finalDiscount = typeof backendDiscountTotal === 'number' && backendDiscountTotal >= 0
      ? backendDiscountTotal
      : discountTotal;
    setSummary({
      subtotal,
      discount: finalDiscount,
      total: subtotal - finalDiscount
    });

    return updatedCart;
  };

  const recalculateCartItems = useCallback(async (updatedItems, discountCodeToUse = undefined, validationData = undefined) => {
    if (updatedItems.length === 0) {
      setIsLoading(false);
      setSummary({ subtotal: 0, discount: 0, total: 0 });
      return;
    }
    setIsLoading(true);
    try {
      const codeToUse = discountCodeToUse !== undefined ? discountCodeToUse : discountCode;
      // Asegurar que el código sea un string válido o null
      const finalDiscountCode = codeToUse && typeof codeToUse === 'string' && codeToUse.trim() 
        ? codeToUse.trim() 
        : null;
      
      const payload = {
        location_id: locationId,
        discount_code: finalDiscountCode, // Pasar el código como string o null
        products: updatedItems.map(item => ({
          id: item.id,
          name: item.alias || item.name,
          price: item.unitPrice || item.price,
          quantity: item.qty
        }))
      };
      console.log('Calculando precios con payload:', payload); // Debug
      console.log('Código de descuento en payload:', payload.discount_code); // Debug
      const response = await calculateDiscountedPrices(payload);
      const productList = response?.data ?? [];
      const merged = mergePricesWithCart(
        updatedItems,
        productList,
        response?.calculatedDiscountTotal
      );

      // Verificar si se aplicó un descuento en la respuesta
      // Si hay un código de descuento en el payload y algún producto tiene descuento aplicado,
      // actualizar el estado para mostrar el mensaje de éxito
      if (finalDiscountCode) {
        // Usar calculatedDiscountTotal del backend cuando venga; si no, calcular desde merged
        const getDiscountAmount = (item) => item.discountAmount ?? item.discount_amount ?? 0;
        const calculatedDiscountTotal =
          typeof response?.calculatedDiscountTotal === 'number'
            ? response.calculatedDiscountTotal
            : merged.reduce((sum, item) => sum + getDiscountAmount(item) * (item.qty || 1), 0);
        const hasTotalDiscount = calculatedDiscountTotal > 0;

        const hasDiscountApplied = merged.some(item => {
          const itemDiscountCode = item.discountCode || item.appliedDiscountCode;
          const codeMatches = itemDiscountCode &&
            itemDiscountCode.toString().toUpperCase() === finalDiscountCode.toUpperCase();
          return getDiscountAmount(item) > 0 && codeMatches;
        });

        const responseHasDiscount = productList.some(item => getDiscountAmount(item) > 0);
        
        console.log('Verificando descuento aplicado:', {
          finalDiscountCode,
          hasDiscountApplied,
          hasTotalDiscount,
          responseHasDiscount,
          calculatedDiscountTotal,
          itemsWithDiscount: merged.filter(item => getDiscountAmount(item) > 0)
        }); // Debug
        
        // Si hay descuento aplicado (por código o por campaña), marcar como válido
        if (hasDiscountApplied || hasTotalDiscount || responseHasDiscount) {
          console.log('Descuento aplicado correctamente desde /sales/calculate'); // Debug
          setDiscountValid(true);
          setDiscountMessage('valid');
          if (!discountData) {
            setDiscountData({ code: finalDiscountCode });
          }
        } else {
          // Código válido pero no se aplicó descuento (ej: "Lleva X paga Y" con pocas unidades del mismo producto)
          const dataToCheck = validationData ?? discountData;
          const isBuyXPayY = dataToCheck?.buyXPayYDetails != null;
          if (isBuyXPayY) {
            setDiscountValid(true);
            setDiscountMessage('requires_quantity');
            if (!discountData && validationData) setDiscountData(validationData);
          } else {
            console.log('Código no aplicado en el cálculo'); // Debug
            if (discountValid === null) {
              setDiscountValid(false);
              setDiscountMessage('invalid');
            }
          }
        }
      } else {
        setDiscountValid(null);
        setDiscountData(null);
        setDiscountMessage(null);
      }

      setIgnoreNextEffect(true);
      onUpdate(merged);
    } catch (err) {
      console.error('Error recalculating prices', err);
    } finally {
      setIsLoading(false);
    }
  }, [discountCode, locationId, onUpdate, discountData, discountValid]);

  const debouncedRecalculateCartItems = useCallback(
    debounce(recalculateCartItems, 500),
    [recalculateCartItems]
  );

  // Guardar referencia para poder cancelar el debounce
  useEffect(() => {
    debouncedRecalculateRef.current = debouncedRecalculateCartItems;
  }, [debouncedRecalculateCartItems]);

  useEffect(() => {
    if (ignoreNextEffect) {
      setIgnoreNextEffect(false);
      return;
    }
  
    if (items.some(i => i._triggerLoading)) {
      setIsLoading(true);
    }
  
    const cleanedItems = items.map(({ _triggerLoading, ...rest }) => rest);
    const prevItems = prevItemsRef.current;
    const hasChanged =
      prevItems.length !== cleanedItems.length ||
      prevItems.some((item, index) => {
        const newItem = cleanedItems[index];
        return !newItem || item.id !== newItem.id || item.qty !== newItem.qty;
      });
  
    if (hasChanged && cleanedItems.length > 0) {
      debouncedRecalculateCartItems(cleanedItems, discountCode);
    } else if (cleanedItems.length === 0) {
      if (debouncedRecalculateRef.current) debouncedRecalculateRef.current.cancel();
      setIsLoading(false);
      setSummary({ subtotal: 0, discount: 0, total: 0 });
    }
  
    prevItemsRef.current = cleanedItems;
  }, [items, debouncedRecalculateCartItems]);  

  const handleIncrement = (productId) => {
    const updatedItems = items.map(item =>
      item.id === productId ? { ...item, qty: item.qty + 1 } : item
    );
    setIsLoading(true);
    onUpdate(updatedItems);
  };

  const handleDecrement = (productId) => {
    const product = items.find(item => item.id === productId);
    const updatedItems = product.qty <= 1
      ? items.filter(item => item.id !== productId)
      : items.map(item =>
          item.id === productId ? { ...item, qty: item.qty - 1 } : item
        );
    setIsLoading(true);
    onUpdate(updatedItems);
  };

  const validateDiscount = async () => {
    try {
      const codeToValidate = discountCode.trim();
      if (!codeToValidate) {
        setDiscountValid(false);
        setDiscountData(null);
        return;
      }

      console.log('Validando código de descuento:', codeToValidate, 'locationId:', locationId); // Debug
      
      if (debouncedRecalculateRef.current) {
        debouncedRecalculateRef.current.cancel();
      }

      let validationData = null;
      try {
        const data = await validateDiscountCode(locationId, codeToValidate);
        console.log('Respuesta de validación:', data); // Debug

        const isValid = data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0);
        validationData = data || null;
        
        if (isValid) {
          console.log('Código válido según validación'); // Debug
          setDiscountValid(true);
          setDiscountData(data);
        } else {
          console.log('Código no válido según validación, pero se pasará al backend para verificación'); // Debug
          setDiscountValid(null);
          setDiscountData(null);
        }
      } catch (validationErr) {
        console.log('Error en validación previa, pero se pasará el código al backend:', validationErr); // Debug
        setDiscountValid(null);
        setDiscountData(null);
      }

      setIsLoading(true);
      console.log('Pasando código al endpoint /sales/calculate:', codeToValidate); // Debug
      recalculateCartItems(items, codeToValidate, validationData);
    } catch (err) {
      console.error('Error en validateDiscount', err);
      console.error('Detalles del error:', err.response?.data); // Debug
      setDiscountValid(false);
      setDiscountData(null);

      setIsLoading(true);
      // Cancelar cualquier debounce pendiente
      if (debouncedRecalculateRef.current) {
        debouncedRecalculateRef.current.cancel();
      }
      // Ejecutar inmediatamente sin debounce
      recalculateCartItems(items.map(i => ({
        ...i,
        discountCode: null,
      })), null);
    }
  };

  const handleApplyDiscount = () => {
    if (!discountCode.trim()) return;
    validateDiscount();
  };

  const handleFinishSale = () => {
    onUpdate([]); // Limpiar carrito
    setSummary({ subtotal: 0, discount: 0, total: 0 }); // Limpiar resumen
    setDiscountCode(''); // Limpiar cupón
    setDiscountValid(null); // Limpiar estado de validación de cupón
    setAppliedStoreCredit(0);
  };

  const renderOptionalSelections = (item) => {
    const optionals = getOptionalSelectionsList(item);
    if (optionals.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {optionals.map((optional, index) => {
          const name = getOptionalSelectionName(optional);
          const extraPrice = Number(optional.extraPrice ?? optional.extra_price ?? 0);

          return (
            <Box
              key={`${name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                pl: 1.5,
              }}
            >
              <Chip
                size="small"
                label={name}
                variant="outlined"
                sx={{
                  height: 24,
                  borderRadius: 1,
                  justifyContent: 'flex-start',
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: 12,
                  },
                }}
              />
              {extraPrice > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  + {formatMoney(extraPrice, currency)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: "1px solid #dcdcdc",
      }}
    >
      <Typography variant="h6" gutterBottom style={{marginTop: "6px", marginLeft: "6px"}}>Items seleccionados</Typography>
      <Divider sx={{ mb: 2 }} />

      {initialStoreCreditCode && (
        <Alert severity="success" onClose={onStoreCreditConsumed} sx={{ mx: 1, mb: 1 }}>
          Saldo de devolución listo para usar. Se aplicará al facturar esta nueva venta.
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
        <List>
          {items.map((item, index) => (
            <ListItem
              key={`${item.id}-${index}`}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 500 }}>{item.alias || item.name}</Typography>
                <Typography variant="body2">
                  {formatMoney(item.unitPrice, currency)}
                  {item.discountAmount > 0 && (
                    <span style={{ color: 'green', marginLeft: 8 }}>
                      -{item.discountAmount.toFixed(1)} ({item.discountCode})
                    </span>
                  )}
                </Typography>
                {renderOptionalSelections(item)}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => handleDecrement(item.id)} disabled={isLoading}>
                  {getIcon('chevronBack')}
                </IconButton>
                <Typography>{item.qty}</Typography>
                <IconButton onClick={() => handleIncrement(item.id)} disabled={isLoading}>
                  {getIcon('chevronForward')}
                </IconButton>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>

      <Box sx={{ p: 2, pt: 1, borderTop: '1px solid #ddd' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
            <CircularProgress size={20} thickness={5} />
          </Box>
        ) : (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2">Subtotal: {formatMoney(summary.total, currency)}</Typography>
            {appliedStoreCredit > 0 && <Typography variant="body2" color="success.dark">Saldo de NC aplicado: −{formatMoney(appliedStoreCredit, currency)}</Typography>}
            <Typography variant="h6">Total: {formatMoney(Math.max(0, summary.total - appliedStoreCredit), currency)}</Typography>
          </Box>
        )}

        <Divider sx={{ mb: 1, mt: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <TextField
            size="small"
            label="Cupón de descuento"
            value={discountCode}
            onChange={(e) => {
              const value = e.target.value;
              setDiscountCode(value);
              setDiscountValid(null);
              setDiscountMessage(null);
              setDiscountTouched(value !== '');
              if (value === '') {
                setDiscountData(null);
                setDiscountMessage(null);
                setDiscountTouched(false);
                // Cancelar cualquier debounce pendiente
                if (debouncedRecalculateRef.current) {
                  debouncedRecalculateRef.current.cancel();
                }
                setIsLoading(true);
                // Ejecutar inmediatamente cuando se elimina el código
                recalculateCartItems(items.map(i => ({
                  ...i,
                  discountCode: null,
                })), null);
              }
            }}
            sx={{ flexGrow: 1, mr: 1 }}
            error={discountValid === false}
            helperText={
              discountMessage === 'requires_quantity'
                ? (() => {
                    const buyQty = discountData?.buyXPayYDetails?.buyQuantity ?? 3;
                    const hasProductFilter = discountData?.buyXPayYDetails?.products?.length > 0 || discountData?.buyXPayYDetails?.categories?.length > 0;
                    return hasProductFilter
                      ? `Para aplicar este descuento agregá al menos ${buyQty} unidades de los productos o categorías elegidos`
                      : `Para aplicar este descuento agregá al menos ${buyQty} productos en total (el descuento se aplica al más barato)`;
                  })()
                : discountValid === false
                ? 'Cupón inválido'
                : discountValid === true && discountMessage === 'valid'
                ? 'Cupón aplicado correctamente ✅'
                : ''
            }
            InputProps={{
              endAdornment: discountCode ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setDiscountCode('');
                    setDiscountValid(null);
                    setDiscountData(null);
                    setDiscountMessage(null);
                    setDiscountTouched(false);
                    // Cancelar cualquier debounce pendiente
                    if (debouncedRecalculateRef.current) {
                      debouncedRecalculateRef.current.cancel();
                    }
                    setIsLoading(true);
                    // Ejecutar inmediatamente cuando se elimina el código
                    recalculateCartItems(items.map(i => ({
                      ...i,
                      discountCode: null,
                    })), null);
                  }}
                >
                  {getIcon('close')}
                </IconButton>
              ) : null,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleApplyDiscount}
          >
            Aplicar
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => setBillingModalOpen(true)}
          disabled={
            items.length === 0 ||
            isLoading ||
            discountValid === false ||
            (discountTouched && discountValid !== true && discountMessage !== 'requires_quantity')
          }
        >
          Facturar
        </Button>
      </Box>

      <BillingModal
        locationId = {locationId}
        open={billingModalOpen}
        onClose={() => { setBillingModalOpen(false); setAppliedStoreCredit(0); }}
        items={items}
        total={summary.total}
        discountCode={discountCode}
        initialStoreCreditCode={initialStoreCreditCode}
        onStoreCreditConsumed={onStoreCreditConsumed}
        onStoreCreditChange={setAppliedStoreCredit}
        onFinish={handleFinishSale}
      />

      {pendingInvoice && pendingInvoice.items && (
        <UndefinedContentModal
          open={undefinedModalOpen}
          onClose={(resolved) => {
            setUndefinedModalOpen(false);
            if (resolved) {
              setPendingInvoice(null);
              handleFinishSale();
            }
          }}
          invoice={pendingInvoice}
          locationId={locationId}
        />
      )}

    </Paper>
  );
};

export default SalesPanel;
